import React, { useEffect, useRef, useState } from "react";
import { Info } from "lucide-react";
import PriceInput from "../components/inputs/PriceInput";
import TipInput from "../components/inputs/TipInput";
import { ItemUI } from "../types/item";
import "./TipTaxTotal.scss";

const SPLIT_INFO =
  "Your share of the tax and tip is calculated proportional to the subtotal: " +
  "if your items are 30% of the subtotal, you cover 30% of the total tax and tip.";

// An (i) icon that toggles the split explanation in a tooltip above it. Each
// icon owns its open state; a press anywhere outside this icon/tooltip —
// including on the other icon — closes it, so only one is ever open.
const InfoIcon: React.FC = () => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (e: PointerEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [open]);

  return (
    <span className="info-wrapper" ref={wrapperRef}>
      <Info
        size={14}
        className="info-icon"
        onClick={() => setOpen((o) => !o)}
      />
      {open && <span className="split-tooltip">{SPLIT_INFO}</span>}
    </span>
  );
};

interface TipTaxTotalProps {
  items: ItemUI[];
  tip: number;
  tax: number;
  setTip: React.Dispatch<React.SetStateAction<number>>;
  setTax: React.Dispatch<React.SetStateAction<number>>;
  editable: boolean;
  // the still-unassigned portion of the check (incl. its tax/tip). Omit to
  // hide the row (before sharing, nothing can be claimed); any number shows it,
  // red while something is still unclaimed, plain once it's $0.00
  unclaimed?: number;
}

const TipTaxTotal: React.FC<TipTaxTotalProps> = ({
  items,
  tip,
  tax,
  setTip,
  setTax,
  editable,
  unclaimed,
}) => {
  const subtotal = items.reduce((sum, item) => sum + item.price, 0);
  const total = subtotal + tip + tax;

  return (
    <div className="tip-tax-total">
      <span className="label">Subtotal</span>
      <span className="value">${subtotal.toFixed(2)}</span>

      <span className="label">Tax <InfoIcon /></span>
      <PriceInput price={tax} setPrice={setTax} editable={editable} />

      <span className="label">Tip <InfoIcon /></span>
      <TipInput
        subtotal={subtotal}
        tip={tip}
        setTip={setTip}
        editable={editable}
      />

      {unclaimed !== undefined && (
        <>
          <span className={`label${unclaimed > 0.005 ? " unclaimed" : ""}`}>
            Unclaimed
          </span>
          <span className={`value${unclaimed > 0.005 ? " unclaimed" : ""}`}>
            ${Math.max(0, unclaimed).toFixed(2)}
          </span>
        </>
      )}

      <span className="label total">Total</span>
      <span className="value total">${total.toFixed(2)}</span>
    </div>
  );
};

export default TipTaxTotal;
